/** Apply imported presentation to the native odds renderer's already formatted markets. */
import type { OddsBoardRenderState } from './normalize';
import { withImageSize } from '../../shared/dom';
type Item = Record<string, any>;
const object = (value: unknown): Item => value && typeof value === 'object' ? value as Item : {};
const fields = (row: HTMLElement, values: Record<string, unknown>) => {
  Object.entries(values).forEach(([hook, value]) => {
    const node = row.querySelector<HTMLElement>(`[data-gwd-${hook}]`);
    if (!node) return;
    if (node instanceof HTMLImageElement) {
      const url = ['group-logo', 'home-logo', 'away-logo'].includes(hook)
        ? withImageSize(String(value || ''))
        : String(value || '');
      if (/^(https?:\/\/|\/[^/])/.test(url)) { node.src = url; node.hidden = false; }
      else { node.removeAttribute('src'); node.hidden = true; }
    } else node.textContent = String(value ?? '-');
  });
};
export const renderCustomOdds = (root: HTMLElement, html: string, data: OddsBoardRenderState, mobile = false, groupCounts?: Map<string, number>): string => {
  const matchTemplate = root.querySelector<HTMLTemplateElement>(mobile ? '[data-gwd-mobile-card-template]' : '[data-gwd-match-template]');
  const groupTemplate = root.querySelector<HTMLTemplateElement>(mobile ? '[data-gwd-mobile-group-template]' : '[data-gwd-group-template]');
  if (!matchTemplate || !groupTemplate) return html;
  const source = document.createElement('template'); source.innerHTML = html;
  const output = document.createElement('div');
  const matches = new Map(data.matches.map((raw) => { const match = object(raw); return [String(match.id ?? match.info?.id ?? ''), match]; }));
  const children = Array.from(source.content.children);
  children.forEach((child, index) => {
    if (!(child instanceof HTMLElement)) return;
    if (child.hasAttribute('data-gwd-odds-league-id')) {
      const row = groupTemplate.content.firstElementChild?.cloneNode(true) as HTMLElement | undefined;
      if (!row) return;
      const id = child.dataset.gwdOddsLeagueId || '';
      let count = 0;
      for (let next = index + 1; next < children.length && !children[next].hasAttribute('data-gwd-odds-league-id'); next++) if (children[next].hasAttribute('data-gwd-odds-match-id')) count++;
      const firstId = (children[index + 1] as HTMLElement | undefined)?.dataset.gwdOddsMatchId;
      if (firstId && groupCounts?.has(firstId)) count = groupCounts.get(firstId)!;
      row.dataset.gwdOddsLeagueId = id;
      fields(row, { 'group-title': child.querySelector('[data-gwd-group-title]')?.textContent || '', 'group-logo': data.competitions[id]?.logo || data.competitions[id]?.icon || '', 'group-count': `${count} trận` });
      const pin = row.querySelector<HTMLElement>('[data-gwd-odds-league-pin]');
      const native = child.querySelector('[data-gwd-odds-league-pin]');
      if (pin && native) { pin.setAttribute('aria-pressed', native.getAttribute('aria-pressed') || 'false'); pin.setAttribute('aria-label', native.getAttribute('aria-label') || 'Ghim giải đấu'); }
      output.append(row); return;
    }
    const id = child.dataset.gwdOddsMatchId;
    if (!id) { output.append(child.cloneNode(true)); return; }
    const row = matchTemplate.content.firstElementChild?.cloneNode(true) as HTMLElement | undefined;
    if (!row) return;
    row.dataset.gwdOddsMatchId = id;
    row.classList.toggle('is-pinned', child.classList.contains('is-pinned'));
    const nativePin = child.querySelector<HTMLElement>('[data-gwd-odds-pin]');
    if (nativePin) {
      const slot = row.querySelector<HTMLElement>('[data-gwd-odds-pin]');
      if (slot) {
        slot.setAttribute('aria-pressed', nativePin.getAttribute('aria-pressed') || 'false');
        slot.setAttribute('aria-label', nativePin.getAttribute('aria-label') || 'Ghim trận đấu');
      }
    }
    const match = matches.get(id) || {}; const info = object(match.info);
    row.dataset.gwdOddsStatusId = String(info.statusId ?? '');
    const values: Record<string, unknown> = {};
    for (const side of ['home', 'away']) {
      const team = data.teams[String(info[`${side}TeamId`] || '')] || object(info[`${side}Team`]);
      const scores = Array.isArray(info[`${side}Scores`]) ? info[`${side}Scores`] : [];
      values[side] = String(team.shortName || '').trim() || String(team.name || '').trim();
      values[`${side}-logo`] = team.logo || '';
      values[`${side}-position`] = info[`${side}Position`] || '';
      values[`${side}-score`] = Number(info.statusId) === 1 ? (side === 'home' ? 'VS' : '') : (scores[0] ?? '-');
      values[`${side}-red`] = scores[2] > 0 ? scores[2] : '';
      values[`${side}-yellow`] = scores[3] > 0 ? scores[3] : '';
      values[`${side}-corner`] = scores[4] >= 0 ? scores[4] : '-';
    }
    const date = new Date(String(info.matchTime || ''));
    const valid = Number.isFinite(date.getTime());
    values.kickoff = valid ? date.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Ho_Chi_Minh' }) : '-';
    values.date = valid ? date.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', timeZone: 'Asia/Ho_Chi_Minh' }) : '-';
    values.half = Number(info.statusId) === 1 ? '-' : `${info.homeScores?.[1] ?? '-'} - ${info.awayScores?.[1] ?? '-'}`;
    values.corners = `${values['home-corner']} - ${values['away-corner']}`;
    values.league = data.competitions[String(info.competitionId || '')]?.name || '-';
    fields(row, values);
    const scalarHooks = [
      'odds-eu-home','odds-eu-draw','odds-eu-away',
      'odds-asia-ft-home','odds-asia-ft-handicap','odds-asia-ft-away',
      'odds-bs-over','odds-bs-line','odds-bs-under','odds-cr-over','odds-cr-line','odds-cr-under',
      'odds-asia-ht-home','odds-asia-ht-handicap','odds-asia-ht-away',
      'odds-bs-ht-over','odds-bs-ht-line','odds-bs-ht-under',
    ];
    scalarHooks.forEach((hook) => {
      const slot = row.querySelector<HTMLElement>(`[data-gwd-${hook}]`);
      const native = child.querySelector<HTMLElement>(`[data-gwd-${hook}]`);
      if (!slot || !native) return;
      slot.innerHTML = native.innerHTML;
      slot.hidden = native.hidden;
      for (const attribute of ['data-gwd-odds-decimal', 'data-gwd-odds-format']) {
        if (native.hasAttribute(attribute)) slot.setAttribute(attribute, native.getAttribute(attribute)!);
        else slot.removeAttribute(attribute);
      }
    });
    const clock = row.querySelector<HTMLElement>('[data-gwd-time]');
    if (clock) { clock.dataset.gwdOddsClock = ''; clock.innerHTML = child.querySelector('[data-gwd-odds-clock]')?.innerHTML || '-'; }
    const marketSources: Record<string, string> = {
      'ft-eu': '[data-gwd-odd-eu]',
      'ft-asia': '[data-gwd-odd-hdp]',
      'ft-bs': '[data-gwd-odd-ou]',
      'ft-cr': '[data-gwd-odd-corner]',
      'ht-asia': '[data-gwd-odd-ht-hdp]',
      'ht-bs': '[data-gwd-odd-ht-ou]',
    };
    Object.entries(marketSources).forEach(([market, selector]) => {
      const slot = row.querySelector(`[data-gwd-template-market-${market}]`);
      const nativeMarket = child.querySelector(selector);
      if (slot && nativeMarket) slot.replaceChildren(nativeMarket.cloneNode(true));
    });
    output.append(row);
  });
  return output.innerHTML;
};
